import React from 'react';
import { Box, Typography } from '@mui/material';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import ConditionGroupHeader, { GroupConfig } from './ConditionGroupHeader';
import ConditionListItem from './ConditionListItem';
import {
  ConditionGroupType,
  createEmptyConditionGroup,
  createEmptyConditionLeaf,
  IConditionGroup,
  IConditionNode,
  IRulesetSchemaField,
  isConditionNotGroup,
} from '../../../types/ruleset-types';

interface ConditionGroupProps {
  group: IConditionGroup;
  schema: { fields: IRulesetSchemaField[] };
  depth: number;
  onChange: (node: IConditionNode) => void;
  onRemove: () => void;
  dataPoolId: string;
  isRoot?: boolean;
  disabled?: boolean;
  insideNot?: boolean;
}

const MAX_NESTING_DEPTH = 20;

const GROUP_CONFIG: Record<ConditionGroupType, GroupConfig> = {
  and: { color: '#1D9AAA', label: 'AND', helperText: 'all conditions must match' },
  or: { color: '#7b1fa2', label: 'OR', helperText: 'any condition must match' },
  not: { color: '#f57c00', label: 'NOT', helperText: 'negates the condition' },
};

const generateId = () => `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const ConditionGroup: React.FC<ConditionGroupProps> = ({
  group,
  schema,
  depth,
  onChange,
  onRemove,
  dataPoolId,
  isRoot = false,
  disabled = false,
  insideNot = false,
}) => {
  const config = GROUP_CONFIG[group.type];

  // NOT groups can only have one child
  const children = isConditionNotGroup(group) ? [group.condition] : group.conditions;
  const canAddMore = group.type !== 'not' || children.length === 0;
  const canAddNestedGroups = depth < MAX_NESTING_DEPTH - 1;
  // Track if we're inside a NOT group (current or parent)
  const isInsideNot = insideNot || group.type === 'not';

  const handleAddCondition = () => {
    const newLeaf = createEmptyConditionLeaf(generateId());
    if (group.type === 'not') {
      onChange({ ...group, condition: newLeaf });
      return;
    }
    onChange({ ...group, conditions: [...group.conditions, newLeaf] });
  };

  const handleAddGroup = (type: ConditionGroupType) => {
    const newGroup = createEmptyConditionGroup(
      generateId(),
      type,
      type === 'not'
        ? [createEmptyConditionLeaf(generateId())]
        : [createEmptyConditionLeaf(generateId()), createEmptyConditionLeaf(generateId())]
    );

    if (group.type === 'not') {
      onChange({ ...group, condition: newGroup });
      return;
    }
    onChange({ ...group, conditions: [...group.conditions, newGroup] });
  };

  const handleTypeChange = (newType: 'and' | 'or') => {
    if (group.type === newType || group.type === 'not') return;
    onChange({ ...group, type: newType });
  };

  const handleChildChange = (index: number, child: IConditionNode) => {
    if (group.type === 'not') {
      onChange({ ...group, condition: child });
      return;
    }
    const newConditions = [...group.conditions];
    newConditions[index] = child;
    onChange({ ...group, conditions: newConditions });
  };

  const handleChildRemove = (index: number) => {
    if (group.type === 'not') {
      onChange({ ...group, condition: createEmptyConditionLeaf(generateId()) });
      return;
    }

    const newConditions = group.conditions.filter((_, i) => i !== index);

    if (!isRoot && newConditions.length === 0) {
      onRemove();
      return;
    }

    if (!isRoot && newConditions.length === 1) {
      onChange(newConditions[0]);
      return;
    }

    onChange({ ...group, conditions: newConditions });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination || group.type === 'not') return;

    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    if (sourceIndex === destIndex) return;

    const newConditions = [...group.conditions];
    const [removed] = newConditions.splice(sourceIndex, 1);
    newConditions.splice(destIndex, 0, removed);
    onChange({ ...group, conditions: newConditions });
  };

  return (
    <Box
      sx={{
        borderLeft: `3px solid ${config.color}`,
        pl: 2,
        py: 1,
      }}
      data-cy={`condition-group-${group.type}`}
    >
      <ConditionGroupHeader
        groupType={group.type}
        config={config}
        isRoot={isRoot}
        childCount={children.length}
        canAddMore={canAddMore}
        canAddNestedGroups={canAddNestedGroups}
        insideNot={isInsideNot}
        disabled={disabled}
        onAddCondition={handleAddCondition}
        onAddGroup={handleAddGroup}
        onTypeChange={handleTypeChange}
        onRemove={onRemove}
      />

      {children.length > 0 ? (
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable
            droppableId={`group-${group.id}`}
            isDropDisabled={disabled || group.type === 'not'}
          >
            {(provided) => (
              <Box
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
              >
                {children.map((child, index) => (
                  <Draggable
                    key={child.id || `condition-${index}`}
                    draggableId={child.id || `condition-${index}`}
                    index={index}
                    isDragDisabled={disabled || group.type === 'not'}
                  >
                    {(provided, snapshot) => (
                      <ConditionListItem
                        child={child}
                        index={index}
                        groupType={group.type}
                        config={config}
                        provided={provided}
                        snapshot={snapshot}
                        schema={schema}
                        depth={depth}
                        dataPoolId={dataPoolId}
                        disabled={disabled}
                        insideNot={isInsideNot}
                        onChildChange={handleChildChange}
                        onChildRemove={handleChildRemove}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </Box>
            )}
          </Droppable>
        </DragDropContext>
      ) : (
        <Box
          sx={{
            p: 3,
            textAlign: 'center',
            backgroundColor: '#fff',
            borderRadius: 1.5,
            border: '1px dashed',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.disabled">
            No conditions. Click + to add one.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ConditionGroup;
